<template>
  <div class="mb-3">
    <h2 class="fw-light d-flex align-items-center mb-0">
      <router-link
        to="/home"
        class="d-flex align-items-center me-2"
        title="Voltar"
      >
        <i class="ph-caret-circle-left ph-2x" />
      </router-link>
      Medições Faturadas
    </h2>
  </div>
  <div class="card border-start border-start-width-2 border-start-primary">
    <div class="card-body">
      <div class="row g-3">
        <div class="col-12 col-md-6">
          <label class="form-label">Data Pagamento Inicial:</label>
          <input type="date" class="form-control" v-model="filters.startDate" />
        </div>
        <div class="col-12 col-md-6">
          <label class="form-label">Data Pagamento Final:</label>
          <input type="date" class="form-control" v-model="filters.endDate" />
        </div>
      </div>
      <div class="text-end mt-3">
        <button
          type="button"
          class="btn btn-success rounded-pill"
          title="Buscar"
          :disabled="disableButton"
          @click="getMeasuresInvoiced"
        >
          <span v-if="disableButton">
            <i class="ph-spinner spinner me-2" />Buscar
          </span>
          <span v-else><i class="ph-magnifying-glass me-2" />Buscar</span>
        </button>
      </div>
    </div>
  </div>
  <LoadingData v-if="isLoading" />
  <DataTableDefault
    v-else
    :dataTable="dataTable"
    :options="optionsDataTable"
    :showButtons="true"
    customClass="table-xs fs-sm"
    @exportToPDF="exportToPDF"
    @exportToExcel="exportToExcel"
  />
</template>

<script>
import LoadingData from '@/components/commons/LoadingData.vue'
import DataTableDefault from '@/components/commons/DataTableDefault.vue'
import { formatToCurrencyBr } from '@/utils/currency'
import {
  dateNowUs30ago,
  dateNowUs,
  dateTimeRmToDateBR,
  dateTimeSqlToDateTimeBR,
  dateTimeSqlToDateBR,
} from '@/utils/date'
import createPDF from '@/utils/pdf'
import createExcel from '@/utils/xlsx'

export default {
  name: 'MeasuresInvoiced',

  components: {
    LoadingData,
    DataTableDefault,
  },

  data() {
    return {
      isLoading: true,
      dataTable: {
        columns: [
          {
            data: 'identificador',
            title: 'Identificador',
          },
          {
            data: 'providerName',
            title: 'Fornecedor',
          },
          {
            data: 'contractNumber',
            title: 'Número',
          },
          {
            data: 'costCenter',
            title: 'Centro de Custo',
          },
          {
            data: 'createdAt',
            title: 'Criação',
          },
          {
            data: 'itemName',
            title: 'Item',
          },
          {
            data: 'totalValue',
            title: 'Valor',
          },
          {
            data: 'approvedManagerBy',
            title: 'Gestor',
          },
          {
            data: 'approvedManagerAt',
            title: 'Data Gestor',
          },
          {
            data: 'approvedAccountingBy',
            title: 'Ger. Financeira',
          },
          {
            data: 'approvedAccountingAt',
            title: 'Data Ger. Financeira',
          },
          {
            data: 'paymentDate',
            title: 'Data Pagamento',
          },
          {
            data: 'link',
            title: 'Ações',
            searchable: false,
            orderable: false,
            render(value, type, row) {
              const path = row.type === 'Contrato' ? 'contracts' : 'moviments'
              return `
                <a href="#" data-item-router="/documents/${path}" data-item-id="${value}" class="badge bg-primary mt-2" title="Detalhar ${row.type} ${value}">
                  Detalhar
                </a>`
            },
          },
        ],
        data: [],
      },
      optionsDataTable: {
        order: [[11, 'desc']],
        columnDefs: [{ targets: [12], className: 'text-center' }],
      },
      filters: {
        startDate: dateNowUs30ago(),
        endDate: dateNowUs(),
      },
      measures: [],
      disableButton: false,
    }
  },

  methods: {
    async getMeasuresInvoiced() {
      try {
        this.isLoading = true
        this.disableButton = true

        this.measures = await this.$store.dispatch(
          'getMeasuresInvoiced',
          this.filters,
        )

        this.dataTable.data = this.measures.map((measure) => {
          return {
            identificador:
              measure.type === 'Contrato' ? measure.contractId : measure.idmov,
            type: measure.type,
            providerName: measure.providerName,
            contractNumber: measure.contractNumber,
            costCenter: measure.costCenter,
            createdAt: dateTimeRmToDateBR(measure.createdAt),
            itemName: measure.itemName,
            totalValue: formatToCurrencyBr(measure.totalValue),
            approvedManagerBy: measure.approvedManagerBy,
            approvedManagerAt: dateTimeSqlToDateTimeBR(
              measure.approvedManagerAt,
            ),
            approvedAccountingBy: measure.approvedAccountingBy,
            approvedAccountingAt: dateTimeSqlToDateTimeBR(
              measure.approvedAccountingAt,
            ),
            paymentDate: dateTimeSqlToDateBR(measure.paymentDate),
            link:
              measure.type === 'Contrato' ? measure.contractId : measure.idmov,
          }
        })

        this.isLoading = false
        this.disableButton = false
      } catch (error) {
        this.disableButton = false
        if (error && error.message) {
          this.$root.$refs.notification.warning(error.message)
        } else {
          this.$root.$refs.notification.error(
            'Não foi possível se conectar ao servidor.',
          )
        }
      }
    },

    exportToPDF() {
      const fileName = 'Medicoes_Faturadas'

      const head = [
        'Identificador',
        'Fornecedor',
        'Número',
        'Centro de Custo',
        'Criação',
        'Item',
        'Valor',
        'Gestor',
        'Data Gestor',
        'Ger. Financeira',
        'Data Ger. Financeira',
        'Data Pagamento',
      ]

      const body = this.measures.map((measure) => {
        return [
          measure.type === 'Contrato' ? measure.contractId : measure.idmov,
          measure.providerName,
          measure.contractNumber,
          measure.costCenter,
          dateTimeRmToDateBR(measure.createdAt),
          measure.itemName,
          formatToCurrencyBr(measure.totalValue),
          measure.approvedManagerBy,
          dateTimeSqlToDateTimeBR(measure.approvedManagerAt),
          measure.approvedAccountingBy,
          dateTimeSqlToDateTimeBR(measure.approvedAccountingAt),
          dateTimeSqlToDateBR(measure.paymentDate),
        ]
      })

      createPDF(fileName, head, body)
    },

    exportToExcel() {
      const fileName = 'Medicoes_Faturadas'

      const data = this.measures.map((measure) => {
        return {
          'Identificador':
            measure.type === 'Contrato' ? measure.contractId : measure.idmov,
          'Fornecedor': measure.providerName,
          'Número': measure.contractNumber,
          'Centro de Custo': measure.costCenter,
          'Criação': dateTimeRmToDateBR(measure.createdAt),
          'Item': measure.itemName,
          'Valor': formatToCurrencyBr(measure.totalValue),
          'Gestor': measure.approvedManagerBy,
          'Data Gestor': dateTimeSqlToDateTimeBR(measure.approvedManagerAt),
          'Ger. Financeira': measure.approvedAccountingBy,
          'Data Ger. Financeira': dateTimeSqlToDateTimeBR(
            measure.approvedAccountingAt,
          ),
          'Data Pagamento': dateTimeSqlToDateBR(measure.paymentDate),
        }
      })

      createExcel(fileName, data)
    },
  },

  mounted() {
    this.getMeasuresInvoiced()
  },
}
</script>
